import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import useStepParam from '@lib/hooks/useStepParam';
import { APP_URLS } from 'constants/urls';
import { Steps } from '@lib/enums/form';
import styles from '@lib/assets/styles/Form.module.scss';

export interface Props {
  loading: boolean;
  stepsCount: Steps;
}

interface Action1Props {
  navigateToPreviousStep: () => void;
  step: number;
}

interface Action4Props {
  isLastStep: boolean;
  loading: boolean;
  navigateToNextStep: () => void;
}

export function Action1({ step, navigateToPreviousStep }: Action1Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (step > Steps.step1) {
    return (
      <Button
        buttonText={t('back')}
        buttonType={ButtonTypes.primaryOutlined}
        fullWidth
        key="back"
        leftIcon="arrow_back"
        onClick={navigateToPreviousStep}
      />
    );
  }

  const onClose = () => {
    navigate(APP_URLS.dashboard.tasks.index.getPathWithQuery());
  };

  return (
    <Button
      buttonText={t('cancel')}
      buttonType={ButtonTypes.secondaryOutlined}
      fullWidth
      key="close"
      leftIcon="close"
      onClick={onClose}
    />
  );
}

export function Action4({
  isLastStep,
  loading,
  navigateToNextStep,
}: Action4Props) {
  const { t } = useTranslation();
  if (isLastStep) {
    return (
      <Button
        buttonText={t('create-task')}
        buttonType={ButtonTypes.primaryFilled}
        disabled={loading}
        fullWidth
        key="submit"
        leftIcon="check"
        type="submit"
      />
    );
  }
  return (
    <Button
      buttonText={t('continue')}
      buttonType={ButtonTypes.primaryFilled}
      fullWidth
      key="next"
      onClick={navigateToNextStep}
      rightIcon="arrow_forward"
    />
  );
}

function TaskAddFormActions({ loading, stepsCount }: Props) {
  const [step, setStep] = useStepParam();
  const isLastStep = step === stepsCount;
  const navigateToStep = (newStep: number) => setStep(newStep);
  const navigateToPreviousStep = () => {
    if (step > 1) {
      navigateToStep(step - 1);
    }
  };
  const navigateToNextStep = () => {
    const nextStep = step + 1;
    if (nextStep <= stepsCount) {
      navigateToStep(step + 1);
    }
  };

  return (
    <ActionsRow
      className={styles.formActions}
      actions={[
        <Action1 step={step} navigateToPreviousStep={navigateToPreviousStep} />,
        <div />,
        <div />,
        <Action4
          isLastStep={isLastStep}
          loading={loading}
          navigateToNextStep={navigateToNextStep}
        />,
      ]}
    />
  );
}

export default TaskAddFormActions;
